import React, { useState, useEffect, lazy, Suspense } from "react";
import { Box, Card, CardContent, Typography, Button, Input, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../AuthContext';
import { useTranslation } from "react-i18next";
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import LanguageSelector from '../components/LanguageSelector'; // Importation du sélecteur de langue existant
import RefreshIcon from '@mui/icons-material/Refresh'; // Ajouter cet import
import WhatsAppIcon from '@mui/icons-material/WhatsApp'; // Ajouter cet import en haut du fichier
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { QRCodeSVG } from 'qrcode.react';  // Ajouter cet import en haut
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';

// Lazy load de l'éditeur
const TinyEditor = lazy(() => import('@tinymce/tinymce-react').then(module => ({
  default: module.Editor
})));

const CommunitySettings = () => {
  const [community, setCommunity] = useState(null);
  const [userData, setUserData] = useState(null);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newLogo, setNewLogo] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [qrCodeText, setQrCodeText] = useState('');
  const [isQrCodeAccordionOpen, setIsQrCodeAccordionOpen] = useState(false);
  const [isLoadingQrText, setIsLoadingQrText] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const fetchWithAuth = useAuthenticatedFetch();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    // Récupérer les informations de l'utilisateur
    fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/user/info`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(t("communitySettings.errors.loadUserData"));
        }
        return res.json();
      })
      .then((data) => {
        setUserData(data);
      })
      .catch((err) => {
        console.error(t("communitySettings.errors.loadUserData"), err);
      });

    // Récupérer les données de la communauté
    fetch(`${process.env.REACT_APP_BASE_URL}/community/getCommunityById/${user.communityId}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(t("communitySettings.errors.loadData"));
        }
        return res.json();
      })
      .then((communityData) => {
        setCommunity(communityData);
        setQrUrl(communityData.qrCode || '');
        setQrCodeText(communityData.qrCodeText || '');
      })
      .catch((err) => {
        console.error(t("communitySettings.errors.loadData"), err);
        setError(t("communitySettings.errors.cannotLoad"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user.communityId, t]);

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    setNewLogo(file);
  };

  const handleLogoUpload = () => {
    if (!newLogo) {
      setError(t("communitySettings.errors.noFile"));
      return;
    }

    const formData = new FormData();
    formData.append("logo", newLogo);
    formData.append("communityId", user.communityId);

    fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/community/uploadCommunityLogo`, {
      method: "POST",
      body: formData,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(t("communitySettings.errors.uploadError"));
        }
        return res.json();
      })
      .then((data) => {
        setCommunity((prevState) => ({
          ...prevState,
          logoPath: data.logoPath,
        }));
        setError(null);
        setSnackbarMessage(t("communitySettings.success.logoUploaded"));
        setSnackbarOpen(true);
      })
      .catch((err) => {
        console.error(t("communitySettings.errors.uploadError"), err);
        setError(t("communitySettings.errors.uploadError"));
      });
  };

  const handleRefreshDisplay = async () => {
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/events/${community.id}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
      });
      
      if (!response.ok) {
        throw new Error(t("communitySettings.errors.refreshError"));
      }
      
      setSnackbarMessage(t("communitySettings.success.refreshSent"));
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Erreur lors du rafraîchissement:', error);
      setError(t("communitySettings.errors.refreshError"));
    }
  };

  const handleSaveQrCode = async () => {
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/community/updateQrCode/${user.communityId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            qrCode: qrUrl,
            qrCodeText: qrCodeText
          }),
        }
      );

      if (!response.ok) {
        throw new Error(t("communitySettings.errors.qrCodeSaveError"));
      }

      setSnackbarMessage(t("communitySettings.success.qrCodeSaved"));
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du QR code:', error);
      setError(t("communitySettings.errors.qrCodeSaveError"));
    }
  };

  // Fonction pour formater l'affichage de la langue
  const formatLanguage = (lang) => {
    switch(lang?.toLowerCase()) {
      case 'en': return t('language.english');
      case 'fr': return t('language.french');
      case 'he': return t('language.hebrew');
      default: return t('language.french'); // Français par défaut
    }
  };

  if (loading) return <Typography>{t("communitySettings.loading")}</Typography>;

  return (
    <Box sx={{ p: 1 }}>
      <Card sx={{ mb: 2 }}>
       <CardContent>
          {/* Conteneur principal avec flexbox */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              direction: i18n.language === 'he' ? "rtl" : "ltr"
            }}
          >
            {/* Texte aligné à gauche */}
            <Box sx={{ 
              textAlign: i18n.language === 'he' ? "right" : "left"
            }}>
              <Typography variant="h5" gutterBottom>
                {t("communitySettings.personalInfo.title")}
              </Typography>
            </Box>

            {/* Language Selector aligné à droite */}
            <Box>
              <LanguageSelector />
            </Box>
          </Box>

          {/* Contenu textuel aligné à droite en hébreu, à gauche dans les autres langues */}
          <Box sx={{ 
            textAlign: i18n.language === 'he' ? "right" : "left",
            direction: i18n.language === 'he' ? "rtl" : "ltr"
          }}>
            <Typography variant="body1">
              {t("communitySettings.personalInfo.email")}: {userData?.email}
            </Typography>
            <Typography variant="body1">
              {t("communitySettings.personalInfo.role")}: {userData?.userRole}
            </Typography>
            <Typography variant="body1">
              {t("communitySettings.personalInfo.name")}: {community?.name}
            </Typography>
            <Typography variant="body1">
              {t("communitySettings.personalInfo.manager")}: {userData?.fullName}
            </Typography>
            <Typography variant="body1">
              {t("communitySettings.personalInfo.nameForUrl")}: {community?.nameForUrl}
            </Typography>
            <Typography variant="body1">
              {t("communitySettings.personalInfo.displayLanguage")}: {formatLanguage(community?.displayLanguage)}
            </Typography>
            <Typography variant="body1">
              {t("communitySettings.personalInfo.secondTemplate")}: {community?.OPT_SecondTemplate ? "✓" : "✗"}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Bloc "Logo de la communauté" */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              textAlign: i18n.language === 'he' ? "right" : "left",
              direction: i18n.language === 'he' ? "rtl" : "ltr"
            }}
          >
            {t("communitySettings.logo.title")}
          </Typography>

          <Grid 
            container 
            spacing={2} 
            direction="column" 
            sx={{ 
              textAlign: i18n.language === 'he' ? "right" : "left",
              direction: i18n.language === 'he' ? "rtl" : "ltr"
            }}
          >
            <Grid item>
              {community?.logoPath ? (
                <img
                  src={community.logoPath}
                  alt={t("communitySettings.logo.alt")}
                  style={{ width: 100, height: 100 }}
                />
              ) : (
                <Typography 
                  variant="body2" 
                  sx={{ mb: 2 }}
                >
                  {t("communitySettings.logo.noLogo")}
                </Typography>
              )}
            </Grid>

            <Grid item>
              <Input
                type="file"
                onChange={handleLogoChange}
                accept="image/*"
                sx={{ 
                  mb: 2,
                  direction: i18n.language === 'he' ? "rtl" : "ltr"
                }}
              />

              {error && (
                <Typography 
                  color="error" 
                  variant="body2"
                  sx={{ 
                    textAlign: i18n.language === 'he' ? "right" : "left",
                    direction: i18n.language === 'he' ? "rtl" : "ltr"
                  }}
                >
                  {error}
                </Typography>
              )}

              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleLogoUpload}
              >
                {t("communitySettings.logo.uploadButton")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {/* Card pour QR Code */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              textAlign: i18n.language === 'he' ? "right" : "left",
              direction: i18n.language === 'he' ? "rtl" : "ltr"
            }}
          >
            {t("communitySettings.qrCode.title")}
          </Typography>

          <Grid 
            container 
            spacing={2} 
            alignItems="center"
            sx={{ 
              textAlign: i18n.language === 'he' ? "right" : "left",
              direction: i18n.language === 'he' ? "rtl" : "ltr"
            }}
          >
            <Grid item xs={12} md={6}>
              <Input
                fullWidth
                placeholder={t("communitySettings.qrCode.urlPlaceholder")}
                value={qrUrl}
                onChange={(e) => setQrUrl(e.target.value)}
                sx={{ 
                  mb: 2,
                  direction: i18n.language === 'he' ? "rtl" : "ltr"
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
              {qrUrl && (
                <Box sx={{ 
                  display: 'inline-block', 
                  p: 2, 
                  bgcolor: 'white',
                  borderRadius: 1
                }}>
                  <QRCodeSVG
                    value={qrUrl}
                    size={75}
                    level="H"
                  />
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Accordion pour le texte détaillé */}
          <Accordion
            expanded={isQrCodeAccordionOpen}
            onChange={(_, expanded) => setIsQrCodeAccordionOpen(expanded)}
            sx={{ mt: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('communitySettings.qrCode.detailText')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {isQrCodeAccordionOpen && (
                <Suspense fallback={<CircularProgress />}>
                  <TinyEditor
                    apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                    value={qrCodeText}
                    onEditorChange={(content) => {
                      setQrCodeText(content);
                    }}
                    init={{
                      height: 500,
                      menubar: false,
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                      ],
                      toolbar: 'undo redo | blocks | ' +
                        'bold italic forecolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat | help',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                    }}
                  />
                </Suspense>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Bouton Save en dessous de l'accordéon */}
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveQrCode}
            >
              {t("communitySettings.qrCode.save")}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Bloc "Lien vers mon écran d'affichage" */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {t("communitySettings.displayScreen.title")}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' } // Colonne sur mobile (xs), ligne sur tablette et plus (sm)
          }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/display/${community?.nameForUrl}?isMainScreen=true`)}
              fullWidth // Pour que les boutons prennent toute la largeur en mode colonne
              sx={{ mb: { xs: 1, sm: 0 } }} // Marge en bas sur mobile uniquement
            >
              {t("communitySettings.displayScreen.button")}
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => navigate(`/displayLayout/${community?.nameForUrl}?isMainScreen=true`)}
              fullWidth
              sx={{ mb: { xs: 1, sm: 0 } }}
            >
              {t("communitySettings.displayScreen.buttonLayout")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshDisplay}
              fullWidth
              sx={{ mb: { xs: 1, sm: 0 } }}
            >
              {t("communitySettings.displayScreen.refresh")}
            </Button>
            <Button
              variant="contained"
              color="whatsapp"
              startIcon={<WhatsAppIcon />}
              onClick={() => {
                const text = encodeURIComponent(`${t("communitySettings.displayScreen.shareText")} https://${window.location.host}/display/${community?.nameForUrl}`);
                window.open(`https://wa.me/?text=${text}`, '_blank');
              }}
              fullWidth
            >
              WhatsApp
            </Button>
          </Box>
        </CardContent>
        {community?.OPT_SecondTemplate && (
          <CardContent>
            <Typography variant="h5" gutterBottom>
              {t("communitySettings.displayScreen.titleSecond")}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 2,
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/display/${community?.nameForUrl}?isMainScreen=true&screenNumber=2`)}
                fullWidth // Pour que les boutons prennent toute la largeur en mode colonne
                sx={{ mb: { xs: 1, sm: 0 } }} // Marge en bas sur mobile uniquement
              >
                {t("communitySettings.displayScreen.buttonSecond")}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshDisplay}
                fullWidth
                sx={{ mb: { xs: 1, sm: 0 } }}
              >
                {t("communitySettings.displayScreen.refresh")}
              </Button>
              <Button
                variant="contained"
                color="whatsapp"
                startIcon={<WhatsAppIcon />}
                onClick={() => {
                  const text = encodeURIComponent(`${t("communitySettings.displayScreen.shareText")} https://${window.location.host}/display/${community?.nameForUrl}?screenNumber=2`);
                  window.open(`https://wa.me/?text=${text}`, '_blank');
                }}
                fullWidth
              >
                WhatsApp
              </Button>
            </Box>
          </CardContent>
        )}
      </Card>
      {/* Snackbar pour tous les messages de succès */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      
    </Box>
  );
};

export default CommunitySettings;